// src/admin/components/HomePageConfig/ColorSection.jsx
import React from "react";
import ColorPicker from "./ColorPicker";

export default function ColorSection({ 
  title, 
  colors, 
  onColorChange,
  useSectionColors = true,
  onToggleSectionColors = null
}) {
  return (
    <div className="hcfg-color-section" style={{ 
      marginTop: "20px", 
      padding: "15px", 
      background: "#f9f9f9", 
      borderRadius: "8px",
      border: "1px solid #eee"
    }}>
      <div style={{ 
        display: "flex", 
        justifyContent: "space-between", 
        alignItems: "center",
        marginBottom: "15px"
      }}>
        <h4 style={{ 
          color: "#333",
          margin: 0,
          display: "flex",
          alignItems: "center",
          gap: "10px"
        }}>
          {title} Colors
          <span style={{
            fontSize: "12px",
            backgroundColor: "#e0f7fa",
            color: "#00796b",
            padding: "2px 8px",
            borderRadius: "12px",
            fontWeight: "normal"
          }}>
            Use "transparent" for no color
          </span>
        </h4>
        
        {onToggleSectionColors && (
          <div style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "10px",
            background: useSectionColors ? "#e8f5e9" : "#f5f5f5",
            padding: "8px 15px",
            borderRadius: "20px",
            border: `1px solid ${useSectionColors ? "#4CAF50" : "#ddd"}`
          }}>
            <label style={{ 
              display: "flex", 
              alignItems: "center", 
              gap: "8px", 
              cursor: "pointer",
              userSelect: "none"
            }}>
              <input
                type="checkbox"
                checked={useSectionColors}
                onChange={(e) => onToggleSectionColors(e.target.checked)}
                style={{ 
                  width: "18px", 
                  height: "18px", 
                  cursor: "pointer",
                  accentColor: "#4CAF50"
                }}
              />
              <span style={{ fontSize: "14px", color: "#333", fontWeight: "500" }}>
                Use Section Colors
              </span>
            </label>
            <span style={{ 
              fontSize: "12px", 
              color: useSectionColors ? "#2E7D32" : "#666",
              fontStyle: "italic"
            }}>
              {useSectionColors ? "✓ Custom" : "→ Global"}
            </span>
          </div>
        )}
      </div>
      
      <div 
        style={{ 
          opacity: useSectionColors ? 1 : 0.5,
          pointerEvents: useSectionColors ? "auto" : "none"
        }}
      >
        {!useSectionColors && (
          <div style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffeaa7",
            borderRadius: "4px",
            padding: "10px",
            marginBottom: "15px",
            display: "flex",
            alignItems: "center",
            gap: "10px"
          }}>
            <span style={{ 
              fontSize: "14px", 
              color: "#856404",
              fontStyle: "italic"
            }}>
              ⓘ Currently using Global Colors. Enable "Use Section Colors" to customize this section separately.
            </span>
          </div>
        )}
        
        <div className="hcfg-row">
          {colors.headingColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Heading Color"
                value={colors.headingColor}
                onChange={(value) => onColorChange("headingColor", value)}
                description="Color for main headings"
                allowTransparent={true}
              />
            </div>
          )}
          
          {colors.subHeadingColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Sub-heading Color"
                value={colors.subHeadingColor}
                onChange={(value) => onColorChange("subHeadingColor", value)}
                description="Color for sub-headings"
                allowTransparent={true}
              />
            </div>
          )}
          
          {colors.contentColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Content Color"
                value={colors.contentColor}
                onChange={(value) => onColorChange("contentColor", value)}
                description="Color for paragraph text"
                allowTransparent={true}
              />
            </div>
          )}
          
          {colors.buttonTextColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Button Text Color"
                value={colors.buttonTextColor}
                onChange={(value) => onColorChange("buttonTextColor", value)}
                description="Color for button text"
                allowTransparent={true}
              />
            </div>
          )}
          
          {colors.buttonBgColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Button Background"
                value={colors.buttonBgColor}
                onChange={(value) => onColorChange("buttonBgColor", value)}
                description="Background color for buttons"
                allowTransparent={true}
              />
            </div>
          )}
          
          {colors.statsColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Stats Color"
                value={colors.statsColor}
                onChange={(value) => onColorChange("statsColor", value)}
                description="Color for statistics/numbers"
                allowTransparent={true}
              />
            </div>
          )}
          
          {colors.iconColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Icon Color"
                value={colors.iconColor}
                onChange={(value) => onColorChange("iconColor", value)}
                description="Color for icons"
                allowTransparent={true}
              />
            </div>
          )}
          
          {colors.reviewerNameColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Reviewer Name Color"
                value={colors.reviewerNameColor}
                onChange={(value) => onColorChange("reviewerNameColor", value)}
                description="Color for reviewer names"
                allowTransparent={true}
              />
            </div>
          )}
          
          {colors.ratingColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Rating Color"
                value={colors.ratingColor}
                onChange={(value) => onColorChange("ratingColor", value)}
                description="Color for star ratings"
                allowTransparent={true}
              />
            </div>
          )}
          
          {colors.serviceTitleColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Service Title Color"
                value={colors.serviceTitleColor}
                onChange={(value) => onColorChange("serviceTitleColor", value)}
                description="Color for service titles"
                allowTransparent={true}
              />
            </div>
          )}
          
          {colors.serviceContentColor !== undefined && (
            <div className="hcfg-col">
              <ColorPicker
                label="Service Content Color"
                value={colors.serviceContentColor}
                onChange={(value) => onColorChange("serviceContentColor", value)}
                description="Color for service descriptions"
                allowTransparent={true}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}